import { Flex } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardBody, CardTitle, Row } from 'reactstrap';
import styled from 'styled-components';

import { useDeviceModels, useFeatureFlags } from '@portals/api/partners';
import { DashboardContentLayout, ModelSelection } from '@portals/framework';
import {
  COFFEE_USAGE_AVG_MOCK,
  OPERATING_HOURS_MOCK,
  YEARLY_REVENUE_MOCK,
} from '@portals/mocks';
import { LineChartCard, MetricCard, PieChartCard, useTheme } from '@portals/ui';
import { getStyledThemeColor } from '@portals/utils';

import {
  useCoffeeUsage,
  useOverviewCardsList,
  useRevenueByProductData,
  useRevenueByProductFamily,
} from './hooks';
import { getDevices } from '../../../../redux/actions/devices';

export function FinanceOverview() {
  const dispatch = useDispatch();
  const { color } = useTheme();
  const features = useFeatureFlags();
  const deviceModels = useDeviceModels();
  const [selectedModel, setSelectedModel] = useState({
    value: null,
    label: features.volvo_demo ? 'All Trucks' : 'All Models',
  });

  useEffect(
    function fetchFinanceData() {
      dispatch(getDevices());
    },
    [dispatch]
  );

  const cardsList = useOverviewCardsList();
  const revenueByProduct = useRevenueByProductData(selectedModel.value);
  const coffeeUsage = useCoffeeUsage();
  const revenueByProductFamily = useRevenueByProductFamily();

  return (
    <DashboardContentLayout
      pageTitle="Finance Overview"
      breadcrumbs={[{ label: 'Store Management', to: '/store-management' }]}
      bg="gray.0"
    >
      <StyledCard className="p-3 m-0">
        <CardTitle className="m-0 page-title mb-2">
          Finance - Overview
        </CardTitle>

        <StyledCardBody>
          <ModelSelection
            deviceModels={deviceModels.data}
            isLoading={deviceModels.isLoading}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
          />

          <Flex mb="xl" wrap="wrap" gap="lg">
            {cardsList.map((card) => (
              <MetricCard {...card} key={card.title} />
            ))}
          </Flex>

          {features.ansa || features.volvo_demo ? (
            <div className="ansa-charts mb-4">
              <LineChartCard
                data={YEARLY_REVENUE_MOCK}
                title="Yearly Revenue"
                withForecast
                dataKeys={[
                  { color: color.primary, key: 'revenue', label: 'Revenue' },
                  { color: color.secondary, key: 'mrr', label: 'MRR' },
                ]}
              />
              <LineChartCard
                data={COFFEE_USAGE_AVG_MOCK}
                title="km Avg."
                withForecast
                options={{ maxY: 500 }}
                dataKeys={[
                  { color: color.primary, key: 'revenue', label: 'Revenue' },
                ]}
              />
            </div>
          ) : (
            <Row className="pr-3 pl-3 d-flex mb-4 flex-row">
              <LineChartCard
                data={YEARLY_REVENUE_MOCK}
                title="Yearly Revenue"
                withForecast
                dataKeys={[
                  { color: color.primary, key: 'revenue', label: 'Revenue' },
                  { color: color.secondary, key: 'mrr', label: 'MRR' },
                ]}
              />
            </Row>
          )}

          {features.ansa || features.volvo_demo ? (
            <div className="ansa-charts">
              <LineChartCard
                data={OPERATING_HOURS_MOCK}
                title="Operating Hours Avg."
                options={{ maxY: 400 }}
                withForecast
                dataKeys={[
                  {
                    color: color.primary,
                    key: 'hours',
                    label: 'Operating Hours',
                  },
                ]}
              />

              <PieChartCard
                data={coffeeUsage}
                fill={color.primary}
                dataKey="value"
                nameKey="label"
                title="Operating Hours by customer"
                emptyTitle=""
              />
            </div>
          ) : (
            <Row className="pie-charts">
              <PieChartCard
                data={revenueByProduct}
                fill={selectedModel?.value ? color.primary : color.secondary}
                dataKey="value"
                nameKey="label"
                title={
                  selectedModel?.value
                    ? 'Revenue by Product'
                    : 'Revenue by Model'
                }
                emptyTitle={
                  selectedModel?.value
                    ? 'No products data for selected model'
                    : 'No models data'
                }
              />

              <PieChartCard
                data={revenueByProductFamily}
                fill={color.primary}
                dataKey="value"
                nameKey="label"
                title="Revenue by Product Family"
                emptyTitle="No products data"
              />
            </Row>
          )}
        </StyledCardBody>
      </StyledCard>
    </DashboardContentLayout>
  );
}

const StyledCard = styled(Card)`
  padding: 30px !important;
  border-left: 1px solid ${getStyledThemeColor('gray200')};
  border-top: 1px solid ${getStyledThemeColor('gray200')};

  .recharts-legend-item {
    text-align: unset;

    .recharts-legend-item-text {
      transform: translateY(2px);
    }
  }
`;

const StyledCardBody = styled(CardBody)`
  padding: 0;

  .ansa-charts,
  .pie-charts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 25px;
  }

  .pie-charts {
    padding: 0 15px;
  }
`;
